import { useCallback, useState } from "react";

import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { addDatadogAction, addDatadogError, buildLocationContext } from "../../services/datadog";
import usePostPrint from "../usePostPrint/usePostPrint";
import { TERMINAL_ENDPOINTS } from "../useTerminalAction/types";
import useTerminalAction from "../useTerminalAction/useTerminalAction";
import { formatSettlementReport } from "./formatSettlementReport";
import { IAcquirerSettlement, ISettlementHeader, SettlementType } from "./types";

const SETTLEMENT_COOLDOWN_MS = 3000;

const buildPrintStub = (body: string): string =>
	JSON.stringify({ body, open_drawer: false, line_spacing: false });

const safePrintReport = async (
	submitPrint: (stubs: string[]) => Promise<void>,
	type: SettlementType,
	acquirers: IAcquirerSettlement[],
	header: ISettlementHeader
): Promise<void> => {
	try {
		const reportBody = formatSettlementReport(type, acquirers, header);

		await submitPrint([buildPrintStub(reportBody)]);
	} catch (err) {
		addDatadogError(err instanceof Error ? err : new Error("Settlement report print failed"), {
			settlementType: type,
			acquirerCount: acquirers.length,
		});
	}
};

const useSubmitSettlement = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const ccSettlement = useTerminalAction(TERMINAL_ENDPOINTS.SETTLEMENT_CREDIT_CARD);
	const netsSettlement = useTerminalAction(TERMINAL_ENDPOINTS.SETTLEMENT_NETS);
	const { submitPrint } = usePostPrint();

	const { state: location } = useConfigContext((s) => s.location);
	const { state: stall } = useConfigContext((s) => s.config.stall);

	const submit = useCallback(async (): Promise<void> => {
		setIsLoading(true);
		setError(null);

		const header: ISettlementHeader = {
			outletName: stall?.name ?? location?.name ?? "",
			stallId: location?.id ?? "",
			machineNo: location?.outletid ?? "",
		};
		const locationCtx = buildLocationContext(location, stall);
		const totalStart = performance.now();

		addDatadogAction("settlement_submit_start", {
			...locationCtx,
			stallName: header.outletName,
		});

		try {
			// Step 1: Credit Card settlement
			const ccResponse = await ccSettlement.submit();
			const ccAcquirers = (ccResponse.message?.messagePayload as IAcquirerSettlement[]) ?? [];

			addDatadogAction("settlement_cc_done", {
				...locationCtx,
				acquirerCount: ccAcquirers.length,
			});

			// Step 2: print CC report (non-blocking on print failure)
			await safePrintReport(submitPrint, "creditcard", ccAcquirers, header);

			// Step 3: 3-second cool-down
			await new Promise((resolve) => setTimeout(resolve, SETTLEMENT_COOLDOWN_MS));

			// Step 4: NETS settlement
			const netsResponse = await netsSettlement.submit();
			const netsAcquirers = (netsResponse.message?.messagePayload as IAcquirerSettlement[]) ?? [];

			addDatadogAction("settlement_nets_done", {
				...locationCtx,
				acquirerCount: netsAcquirers.length,
			});

			// Step 5: print NETS report
			await safePrintReport(submitPrint, "nets", netsAcquirers, header);

			addDatadogAction("settlement_completed", {
				...locationCtx,
				totalDurationMs: performance.now() - totalStart,
			});

			setIsLoading(false);
		} catch (err) {
			const message = err instanceof Error ? err.message : "Settlement failed";

			setError(message);
			setIsLoading(false);
			throw new Error(message);
		}
	}, [ccSettlement, netsSettlement, submitPrint, stall, location]);

	return { submit, isLoading, error };
};

export default useSubmitSettlement;
